import type { InputHTMLAttributes, ReactNode } from "react";
import { useId } from "react";

export interface FormFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  inputSize?: "sm" | "md" | "lg";
}

export function FormField({
  label,
  hint,
  error,
  inputSize = "md",
  id,
  className,
  ...rest
}: FormFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div
      className={["rp-field", error && "rp-field--error", className]
        .filter(Boolean)
        .join(" ")}
    >
      <label htmlFor={inputId} className="rp-field__label">
        {label}
      </label>
      <input
        {...rest}
        id={inputId}
        aria-invalid={!!error || undefined}
        aria-describedby={
          [hintId, errorId].filter(Boolean).join(" ") || undefined
        }
        className={`rp-field__input rp-field__input--${inputSize}`}
      />
      {hint && !error && (
        <p id={hintId} className="rp-field__hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="rp-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
